import React, { useState } from 'react';
import { Users, Search, Filter, BarChart, Star, MessageSquare, Download, ArrowRight, Brain, Target, Award } from 'lucide-react';

type Candidate = {
  id: string;
  name: string;
  role: string;
  skills: string[];
  aiScore: number;
  projectsCompleted: number;
  matchScore: number;
  experience: string;
  availability: string;
  location: string;
  salary: string;
  portfolio: string;
};

const TalentDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  const mockCandidates: Candidate[] = [
    {
      id: '1',
      name: 'Ana García',
      role: 'Product Manager',
      skills: ['Estrategia de Producto', 'Análisis de Datos', 'Gestión de Stakeholders'],
      aiScore: 92,
      projectsCompleted: 15,
      matchScore: 95,
      experience: '5 años',
      availability: 'Inmediata',
      location: 'Madrid',
      salary: '45-55k €/año',
      portfolio: 'https://portfolio.anagarcia.com'
    },
    {
      id: '2',
      name: 'Carlos Martínez',
      role: 'Desarrollador Full Stack',
      skills: ['React', 'Node.js', 'TypeScript', 'AWS'],
      aiScore: 88,
      projectsCompleted: 12,
      matchScore: 87,
      experience: '3 años',
      availability: '1 mes',
      location: 'Barcelona',
      salary: '40-50k €/año',
      portfolio: 'https://github.com/carlosmartinez'
    },
    {
      id: '3',
      name: 'Laura Sánchez',
      role: 'UX/UI Designer',
      skills: ['Figma', 'User Research', 'Design Systems'],
      aiScore: 94,
      projectsCompleted: 18,
      matchScore: 91,
      experience: '4 años',
      availability: 'Inmediata',
      location: 'Valencia',
      salary: '35-45k €/año',
      portfolio: 'https://behance.net/laurasanchez'
    }
  ];

  const filteredCandidates = mockCandidates.filter(candidate => {
    const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         candidate.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || candidate.role === selectedRole;
    const matchesSkills = selectedSkills.length === 0 || 
                         selectedSkills.every(skill => candidate.skills.includes(skill));
    
    return matchesSearch && matchesRole && matchesSkills;
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-foreground">Panel de Talento</h1>
          <div className="flex items-center space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
              <Download className="h-4 w-4 mr-2" />
              Exportar Reporte
            </button>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar candidatos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <Search className="absolute right-3 top-2.5 h-5 w-5 text-foreground/50" />
            </div>
            
            <div className="mt-4 md:mt-0 flex space-x-4">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="px-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="all">Todos los roles</option>
                <option value="Product Manager">Product Manager</option>
                <option value="Desarrollador">Desarrollador</option>
                <option value="Designer">Designer</option>
              </select>
              
              <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-secondary/50">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {filteredCandidates.map((candidate) => (
            <div
              key={candidate.id}
              className="dark-card p-6 hover:border-accent/50 hover:shadow-sm transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-medium text-foreground">{candidate.name}</h3>
                  <p className="text-foreground/70">{candidate.role}</p>
                  
                  <div className="mt-2 flex items-center space-x-4">
                    <div className="flex items-center">
                      <Brain className="h-4 w-4 text-accent mr-1" />
                      <span className="text-sm text-foreground/70">Score IA: {candidate.aiScore}</span>
                    </div>
                    <div className="flex items-center">
                      <Target className="h-4 w-4 text-accent mr-1" />
                      <span className="text-sm text-foreground/70">{candidate.projectsCompleted} proyectos</span>
                    </div>
                    <div className="flex items-center">
                      <Award className="h-4 w-4 text-accent mr-1" />
                      <span className="text-sm text-foreground/70">Match: {candidate.matchScore}%</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-accent hover:bg-accent/90">
                    Ver Perfil
                  </button>
                  <button className="inline-flex items-center px-3 py-1 border border-accent text-sm font-medium rounded-md text-accent hover:bg-accent/10">
                    Contactar
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex flex-wrap gap-2">
                  {candidate.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-foreground/70">Experiencia</p>
                  <p className="text-sm font-medium text-foreground">{candidate.experience}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/70">Disponibilidad</p>
                  <p className="text-sm font-medium text-foreground">{candidate.availability}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/70">Ubicación</p>
                  <p className="text-sm font-medium text-foreground">{candidate.location}</p>
                </div>
                <div>
                  <p className="text-sm text-foreground/70">Salario esperado</p>
                  <p className="text-sm font-medium text-foreground">{candidate.salary}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TalentDashboard;